import { ChangeEvent, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import TextField from "@mui/material/TextField";

import { colors } from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import {
    TOOLTIP_PLACEMENT,
    TooltipOptional
} from "components/DesignComponents/Tooltips";
import { useFormattedNumber } from "shared";

import { BUTTON_ACTIONS, StepperProps } from "./types";

const Stepper = ({
    value,
    iconLeft,
    iconRight,
    iconFirst,
    iconLast,
    minValue,
    maxValue,
    tooltipLeft,
    tooltipRight,
    tooltipFirst,
    tooltipLast,
    buttonClick,
    customValue,
    ...props
}: StepperProps) => {
    const { black, gray200, gray700, gray900 } = colors;

    const { renderFormattedNumber } = useFormattedNumber();
    const { t } = useTranslation();

    const [currentPage, setCurrentPage] = useState(value);

    useEffect(() => {
        setCurrentPage(value);
    }, [value]);

    const isLeftBtnDisabled = value <= minValue;
    const isRightBtnDisabled = value >= maxValue;

    const getPage = (input: number) => {
        if (maxValue && input > maxValue) {
            return maxValue;
        }

        return input;
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const userInput = +e.target.value.replace(/\D/g, "");
        const newValue = getPage(userInput);

        maxValue !== 0 && setCurrentPage(newValue);
    };

    const handleCustomValue = (currentPage: number) => {
        const updatedCurrentPage =
            currentPage < minValue ? minValue : currentPage;

        setCurrentPage(updatedCurrentPage);
        customValue(updatedCurrentPage);
    };

    return (
        <div
            {...props}
            css={css({
                display: "flex",
                alignItems: "center",

                button: {
                    color: black
                }
            })}
        >
            <TooltipOptional
                placement={TOOLTIP_PLACEMENT.Top}
                title={tooltipFirst ?? ""}
                isHidden={isLeftBtnDisabled}
                disableTouchListener
            >
                <span>
                    <Button
                        disabled={isLeftBtnDisabled}
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Small}
                        variant={BUTTON_VARIANTS.IconOnly}
                        icon={iconFirst}
                        onClick={() => buttonClick(BUTTON_ACTIONS.FirstPage)}
                        css={css({
                            marginRight: "8px"
                        })}
                    />
                </span>
            </TooltipOptional>

            <TooltipOptional
                placement={TOOLTIP_PLACEMENT.Top}
                title={tooltipLeft ?? ""}
                isHidden={isLeftBtnDisabled}
                disableTouchListener
            >
                <span>
                    <Button
                        disabled={isLeftBtnDisabled}
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Small}
                        variant={BUTTON_VARIANTS.IconOnly}
                        icon={iconLeft}
                        onClick={() => buttonClick(BUTTON_ACTIONS.PreviousPage)}
                    />
                </span>
            </TooltipOptional>

            <div
                css={css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "36px",
                    minWidth: "36px",
                    padding: "0 8px",
                    boxSizing: "border-box",

                    span: {
                        color: gray900,
                        fontSize: "14px",
                        fontWeight: "500",
                        lineHeight: "20px",
                        letterSpacing: "0.1px",
                        marginLeft: "4px",
                        whiteSpace: "nowrap"
                    }
                })}
            >
                {maxValue > 1 ? (
                    <TextField
                        variant="filled"
                        size="small"
                        onChange={handleChange}
                        value={
                            currentPage < minValue
                                ? ""
                                : renderFormattedNumber(currentPage)
                        }
                        onBlur={() => handleCustomValue(currentPage)}
                        onKeyDown={ev =>
                            ev.key === "Enter" && handleCustomValue(currentPage)
                        }
                        css={css({
                            width: `${currentPage.toString().length + 1}ch`,

                            transition:
                                "background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",

                            "&:hover": {
                                backgroundColor: gray200,
                                borderRadius: "6px 6px 0 0"
                            },

                            "& .MuiInputBase-input": {
                                height: "unset",
                                fontSize: "14px",
                                fontWeight: "500",
                                lineHeight: "20px",
                                letterSpacing: "0.1px",
                                textAlign: "center"
                            },

                            "& .MuiFilledInput-input": {
                                padding: "8px 4px"
                            },

                            "& .MuiFilledInput-root::before": {
                                borderBottom: `1px solid ${gray700}`
                            },

                            "& .MuiFilledInput-root::after": {
                                borderBottom: "unset"
                            },

                            "& .MuiFilledInput-root, &.Mui-focused": {
                                backgroundColor: "transparent"
                            }
                        })}
                    />
                ) : (
                    <span>
                        {maxValue === 0
                            ? 0
                            : renderFormattedNumber(currentPage)}
                    </span>
                )}

                <span>
                    {t("Button##pages of", {
                        total: renderFormattedNumber(maxValue)
                    })}
                </span>
            </div>

            <TooltipOptional
                placement={TOOLTIP_PLACEMENT.Top}
                title={tooltipRight ?? ""}
                isHidden={isRightBtnDisabled}
                disableTouchListener
            >
                <span>
                    <Button
                        disabled={isRightBtnDisabled}
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Small}
                        variant={BUTTON_VARIANTS.IconOnly}
                        icon={iconRight}
                        onClick={() => buttonClick(BUTTON_ACTIONS.NextPage)}
                        css={css({
                            marginRight: "8px"
                        })}
                    />
                </span>
            </TooltipOptional>

            <TooltipOptional
                placement={TOOLTIP_PLACEMENT.Top}
                title={tooltipLast ?? ""}
                isHidden={isRightBtnDisabled}
                disableTouchListener
            >
                <span>
                    <Button
                        disabled={isRightBtnDisabled}
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Small}
                        variant={BUTTON_VARIANTS.IconOnly}
                        icon={iconLast}
                        onClick={() => buttonClick(BUTTON_ACTIONS.LastPage)}
                    />
                </span>
            </TooltipOptional>
        </div>
    );
};

export default Stepper;
